import { RECENT_SEARCHES } from '../constants/local_storage_constants';

export const MAX_RECENT_SEARCHES = 3;
export const MAX_SEARCH_LANDING_ITEMS = 6;
export const MAX_SEARCH_LANDING_PODCASTS = 6;
export const MAX_SEARCH_LANDING_SPEAKERS = 6;

const browserStorage = () => {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch (error) {
        return null;
    }
};

export const compactSearchQuery = (query) => (
    typeof query === 'string' ? query.trim().replace(/\s+/g, ' ') : ''
);

const normalizedQueryIdentity = (query) => compactSearchQuery(query).toLowerCase();

export const sanitizeRecentSearches = (queries) => {
    if (!Array.isArray(queries)) {
        return [];
    }

    const seen = new Set();
    const sanitized = [];

    for (const value of queries) {
        const query = compactSearchQuery(value);
        const identity = normalizedQueryIdentity(query);
        if (!query || seen.has(identity)) {
            continue;
        }

        seen.add(identity);
        sanitized.push(query);
        if (sanitized.length === MAX_RECENT_SEARCHES) {
            break;
        }
    }

    return sanitized;
};

export const readRecentSearches = (storage = browserStorage()) => {
    if (!storage) {
        return [];
    }

    try {
        return sanitizeRecentSearches(JSON.parse(storage.getItem(RECENT_SEARCHES)));
    } catch (error) {
        return [];
    }
};

export const writeRecentSearches = (queries, storage = browserStorage()) => {
    const sanitized = sanitizeRecentSearches(queries);
    if (!storage) {
        return sanitized;
    }

    try {
        storage.setItem(RECENT_SEARCHES, JSON.stringify(sanitized));
    } catch (error) {
        // Search continues to work when storage is disabled or full.
    }
    return sanitized;
};

export const addRecentSearch = (query, storage = browserStorage()) => {
    const compactedQuery = compactSearchQuery(query);
    if (!compactedQuery) {
        return readRecentSearches(storage);
    }

    const identity = normalizedQueryIdentity(compactedQuery);
    const nextQueries = [
        compactedQuery,
        ...readRecentSearches(storage).filter(
            (recentQuery) => normalizedQueryIdentity(recentQuery) !== identity,
        ),
    ];
    return writeRecentSearches(nextQueries, storage);
};

export const removeRecentSearch = (query, storage = browserStorage()) => {
    const identity = normalizedQueryIdentity(query);
    return writeRecentSearches(
        readRecentSearches(storage).filter(
            (recentQuery) => normalizedQueryIdentity(recentQuery) !== identity,
        ),
        storage,
    );
};

export const clearRecentSearches = (storage = browserStorage()) => {
    if (!storage) {
        return [];
    }

    try {
        storage.removeItem(RECENT_SEARCHES);
    } catch (error) {
        // Keep the in-memory landing state usable when storage is unavailable.
    }
    return [];
};

const normalizedPodcast = (podcast) => {
    const id = compactSearchQuery(podcast && podcast.id);
    const name = compactSearchQuery(podcast && podcast.name);
    if (!id || !name) {
        return null;
    }

    return {
        ...podcast,
        id,
        name,
        creator: compactSearchQuery(podcast && podcast.creator),
        imageURL: compactSearchQuery(podcast && podcast.imageURL),
    };
};

const normalizedSpeaker = (speaker) => {
    const speakerId = compactSearchQuery(speaker && speaker.speakerId);
    const name = compactSearchQuery(speaker && speaker.name);
    if (!speakerId || !name) {
        return null;
    }

    return {
        ...speaker,
        speakerId,
        name,
        description: compactSearchQuery(speaker && speaker.description),
        topics: Array.isArray(speaker && speaker.topics)
            ? speaker.topics.map(compactSearchQuery).filter(Boolean)
            : [],
    };
};

const normalizedTopic = (topic) => {
    const query = compactSearchQuery(topic && topic.query);
    if (!query) {
        return null;
    }

    return {
        ...topic,
        query,
        label: compactSearchQuery(topic && topic.label) || query,
    };
};

const uniqueItems = (items, normalize, identity, limit) => {
    if (!Array.isArray(items) || limit <= 0) {
        return [];
    }

    const values = [];
    const seen = new Set();

    for (const item of items) {
        const value = normalize(item);
        const key = value && identity(value);
        if (!value || !key || seen.has(key)) {
            continue;
        }

        seen.add(key);
        values.push(value);
        if (values.length === limit) {
            break;
        }
    }

    return values;
};

export const sanitizeSearchLanding = (landing) => ({
    podcasts: uniqueItems(
        landing && landing.podcasts,
        normalizedPodcast,
        (podcast) => podcast.id,
        MAX_SEARCH_LANDING_PODCASTS,
    ),
    speakers: uniqueItems(
        landing && landing.speakers,
        normalizedSpeaker,
        (speaker) => speaker.speakerId,
        MAX_SEARCH_LANDING_SPEAKERS,
    ),
    topics: uniqueItems(
        landing && landing.topics,
        normalizedTopic,
        (topic) => topic.query.toLowerCase(),
        MAX_SEARCH_LANDING_ITEMS,
    ),
});
